var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/x-livekit.ts
import "@adornis/ass/style.js";
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import { ComponentSizeController } from "@adornis/chemistry/controllers/component-size-controller.js";
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-button.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import "@adornis/chemistry/renderable.js";
import { html, nothing } from "lit";
import { customElement } from "lit/decorators.js";
import { property } from "lit/decorators/property.js";
import { state } from "lit/decorators/state.js";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { Track } from "livekit-client";
import { getLivekitRoomToken } from "../db/livekit.js";
import { LivekitController } from "./livekitController.js";
import { localStreamSettings, LocalStreamSettingsDialog } from "./x-local-stream-settings.js";
var XLivekit = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.ownVideoElement = createRef();
    this.ownScreenShareElement = createRef();
    this.livekitController = new LivekitController(this, {
      ownCameraVideoRef: this.ownVideoElement,
      createLocalTracks: async (participant) => {
        const tracks = await participant.createTracks({
          audio: { deviceId: { ideal: localStreamSettings.getValue().audioIn } },
          video: { deviceId: { ideal: localStreamSettings.getValue().videoIn } }
        });
        for (const track of tracks)
          await track.mute();
        return tracks;
      }
    });
    this.size = new ComponentSizeController(this, { debounceTime: 200 });
  }
  async joinRoom(roomID) {
    this.lastRoomID = roomID;
    await this._joinRoom();
  }
  async _joinRoom() {
    if (!this.lastRoomID)
      throw new Error("roomID not set!");
    const token = await getLivekitRoomToken(this.lastRoomID)();
    this.livekitController.joinRoom(token);
  }
  // Render stuffs
  statusDisplay() {
    if (!this.livekitController.isConnected())
      return html`
        <x-flex
          padding="sm"
          ${css({ position: "absolute", bottom: "1em", left: "1em", background: "white", borderRadius: "5px" })}
        >
          <x-text> Warte auf Raum... </x-text>
        </x-flex>
      `;
    const errors = this.livekitController.getLocalErrors();
    if (errors.size)
      return html`<x-flex
        padding="sm"
        ${css({ position: "absolute", bottom: "1em", left: "1em", background: "white", borderRadius: "5px" })}
      >
        <x-text> Kein lokaler Stream erstellt. </x-text>
      </x-flex>`;
    const localState = this.livekitController.getLocalTrackState();
    return html`
      <x-flex
        padding="sm"
        ${css({ position: "absolute", bottom: "1em", left: "1em", background: "white", borderRadius: "5px" })}
      >
        <x-flex horizontal crossaxis-center space="md">
          <x-button
            mode="icon"
            icon=${localState.microphone ? "mic" : "mic_off"}
            @click=${() => {
      this.livekitController.toggleMicrophone();
    }}
          >
          </x-button>
          <x-button
            mode="icon"
            icon=${localState.camera ? "videocam" : "videocam_off"}
            @click=${() => {
      this.livekitController.toggleCamera();
    }}
          >
          </x-button>
          </x-button>
          <x-button
            mode="icon"
            icon=${/*this.rtcController.memberInfo.screenshare ? 'stop_screen_share' : */
    "screen_share"}
            @click=${async () => {
    }}
          >
          </x-button>
          <x-button
            mode="icon"
            icon="settings"
            @click=${async () => {
      await LocalStreamSettingsDialog.showPopup();
      void this._joinRoom();
    }}
          ></x-button>
          <x-button
            mode="icon"
            icon="logout"
            @click=${async () => {
      this.livekitController.disconnect(true);
    }}
          ></x-button>
        </x-flex>
      </x-flex>
    `;
  }
  emptyRoom() {
    return html`
      <x-flex flex center crossaxis-center>
        <x-text> Nobody here 🦗 </x-text>
      </x-flex>
    `;
  }
  retryStream() {
    const errors = this.livekitController.getLocalErrors();
    return html`
      <x-flex flex center crossaxis-center space="md">
        <x-text> Could not get local video feed: </x-text>
        ${[...errors].map(
      (error) => html`<pre ${css({ marginTop: "0" })}>  ${error.stack ? error.stack : error.message}</pre>`
    )}
        <x-button mode="outline" @click=${() => this._joinRoom()}> Try again </x-button>
        <x-button
          mode="outline"
          @click=${async () => {
      await LocalStreamSettingsDialog.showPopup();
      await this._joinRoom();
    }}
        >
          Open settings
        </x-button>
      </x-flex>
    `;
  }
  remoteVideo(element) {
    return element;
  }
  render() {
    const memberCount = this.livekitController.getRemoteParticipantCount();
    const { columns, elementSize, rows } = this.computeGridParams(memberCount);
    const localErrors = this.livekitController.getLocalErrors();
    const localState = this.livekitController.getLocalTrackState();
    return html`
      <x-grid
        columns="repeat(${columns}, ${elementSize})"
        rows="repeat(${rows}, ${elementSize})"
        space="md"
        ${css({ width: "100%", height: "100%", position: "relative" })}
      >
        ${localErrors.size || memberCount === 0 ? html`
              <x-flex ${css({ position: "absolute", top: "0", left: "0", bottom: "0", right: "0" })}>
                ${localErrors.size ? this.retryStream() : this.emptyRoom()}
              </x-flex>
            ` : repeat(this.livekitController.remoteMedia(), ([member, boxElement]) => this.remoteVideo(boxElement))}
        <video
          ${css({
      display: localState.camera ? "block" : "none",
      position: "absolute",
      borderRadius: "5px",
      objectFit: "cover",
      bottom: "1em",
      right: "1em",
      width: "20vmin"
    })}
          ${ref(this.ownVideoElement)}
          autoplay
          muted
        ></video>
        <video
          ${css({
      display: (
        /*this.rtcController.memberInfo.screenshare ? 'block' : */
        "none"
      ),
      position: "absolute",
      borderRadius: "5px",
      bottom: "1em",
      right: "calc(2em + 10vmin)",
      width: "10vmin"
    })}
          ${ref(this.ownScreenShareElement)}
          autoplay
          muted
        ></video>
        ${this.statusDisplay()}
      </x-grid>
    `;
  }
  computeGridParams(memberCount) {
    let elementSize;
    let columns;
    let rows;
    if (typeof this.size.width === "undefined" || typeof this.size.height === "undefined")
      throw new Error("cannot layout without size info!");
    if (memberCount > 1) {
      const gap = this.spacing.md.number;
      const columnsW = Math.ceil(Math.sqrt(memberCount * this.size.width / this.size.height));
      const rowsW = Math.ceil(memberCount / columnsW);
      const elementSizeW = Math.min(
        (this.size.width - gap * (columnsW - 1)) / columnsW,
        (this.size.height - gap * (rowsW - 1)) / rowsW
      );
      const rowsH = Math.ceil(Math.sqrt(memberCount * this.size.height / this.size.width));
      const columnsH = Math.ceil(memberCount / rowsH);
      const elementSizeH = Math.min(
        (this.size.width - gap * (columnsH - 1)) / columnsH,
        (this.size.height - gap * (rowsH - 1)) / rowsH
      );
      if (elementSizeW > elementSizeH) {
        columns = columnsW;
        rows = rowsW;
        elementSize = elementSizeW + "px";
      } else {
        columns = columnsH;
        rows = rowsH;
        elementSize = elementSizeH + "px";
      }
    } else {
      columns = 1;
      rows = 1;
      elementSize = "1fr";
    }
    return { columns, elementSize, rows };
  }
};
XLivekit = __decorateClass([
  customElement("x-livekit")
], XLivekit);
var XLivekitBox = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.loading = false;
  }
  styles() {
    return [
      {
        ":host": { display: "block", position: "relative", height: "100%", width: "100%", minHeight: "0" },
        "video[camera]": this.mainVideoCSS(),
        "video[screen]": this.screenshareVideoCSS()
      }
    ];
  }
  connectedCallback() {
    super.connectedCallback();
    if (!this.participant)
      throw new Error("x-livekit-box requires participant before attaching it into the DOM");
  }
  trackSubscribed(track, publication) {
    if (publication.source === Track.Source.Camera) {
      this._cameraElement = track.attach();
      this._cameraElement.setAttribute("camera", "");
    }
    if (publication.source === Track.Source.Microphone) {
      this._audioElement = track.attach();
      this._audioElement.setAttribute("microphone", "");
    }
  }
  render() {
    if (!this.participant)
      return nothing;
    return html`
      <x-flex horizontal ${css({ width: "100%", height: "100%", opacity: this._cameraElement ? "1" : "0" })}>
        ${this._cameraElement}
      </x-flex>
      ${this.controlOverlay()} ${this.stateOverlays()} ${this.loadingOverlay(this.loading)}
    `;
  }
  mainVideoCSS() {
    return {
      borderRadius: "5px",
      width: "100%",
      height: "100%",
      objectFit: "cover"
    };
  }
  screenshareVideoCSS() {
    return {
      display: "none"
    };
  }
  controlOverlay() {
    return nothing;
  }
  stateOverlays() {
    if (!this.participant)
      return nothing;
    const microphoneEnabled = this.participant.getTrackPublication(Track.Source.Microphone)?.isMuted === false;
    const cameraEnabled = this.participant.getTrackPublication(Track.Source.Camera)?.isMuted === false;
    return html`${microphoneEnabled && !cameraEnabled ? this.audioOnly() : nothing}
    ${cameraEnabled && !microphoneEnabled ? this.mutedOverlay() : nothing}
    ${!cameraEnabled && !microphoneEnabled ? this.noTracksOverlay() : nothing}`;
  }
  audioOnly() {
    return html`
      <x-flex
        ${css({ position: "absolute", top: "0", left: "0", width: "100%", height: "100%", fontSize: "3em" })}
        center
        crossaxis-center
      >
        <x-icon> call </x-icon>
      </x-flex>
    `;
  }
  mutedOverlay() {
    return html`
      <x-flex
        padding="md"
        ${css({
      position: "absolute",
      top: "1em",
      right: "1em",
      background: "white",
      fontSize: "3em",
      borderRadius: "5px"
    })}
      >
        <x-icon style="color: darkred;"> mic_off </x-icon>
      </x-flex>
    `;
  }
  noTracksOverlay() {
    return html`
      <x-flex
        ${css({ position: "absolute", top: "0", left: "0", width: "100%", height: "100%", fontSize: "3em" })}
        horizontal
        center
        crossaxis-center
      >
        <x-icon style="color: darkred;"> mic_off </x-icon>
        <x-icon style="color: darkred;"> videocam_off </x-icon>
      </x-flex>
    `;
  }
  // TODO maybe generate an image out of the video like here https://stackoverflow.com/a/66499811 and use that as the loading background so we dont flash at all
  loadingOverlay(visible) {
    return html`
      <x-flex
        ${css({
      position: "absolute",
      top: "0",
      left: "0",
      width: "100%",
      height: "100%",
      transition: "background 150ms",
      background: visible ? this.colors.neutral.shade(200).opacity(0.9) : ""
    })}
        horizontal
        center
        crossaxis-center
      >
        ${visible ? html` <x-loader></x-loader> ` : nothing}
      </x-flex>
    `;
  }
};
__decorateClass([
  state()
], XLivekitBox.prototype, "participant", 2);
__decorateClass([
  property({ attribute: true, type: Boolean })
], XLivekitBox.prototype, "loading", 2);
__decorateClass([
  state()
], XLivekitBox.prototype, "_cameraElement", 2);
__decorateClass([
  state()
], XLivekitBox.prototype, "_audioElement", 2);
XLivekitBox = __decorateClass([
  customElement("x-livekit-box")
], XLivekitBox);
export {
  XLivekit,
  XLivekitBox
};
